import moment from "moment";
import { BarDataItem, Day, HolidayCountryType } from "./types";

const DISPLAY_DATE_FORMAT = "DD/MM/YYYY";

export const toApiStartDate = (displayDate: string): string =>
  moment.utc(displayDate, DISPLAY_DATE_FORMAT).startOf("day").toISOString();

export const toApiEndDate = (displayDate: string): string =>
  moment.utc(displayDate, DISPLAY_DATE_FORMAT).endOf("day").toISOString();

export const formatMinutes = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}:${String(m).padStart(2, "0")}`;
};

/**
 * Generates a deterministic HSL color from a name string.
 * Uses a djb2-style hash so the same name always produces the same hue,
 * giving consistent, visually distinct colors similar to Jira avatar initials.
 */
const colorFromName = (name: string): string => {
  let hash = 5381;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 33) ^ name.charCodeAt(i);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 65%, 45%)`;
};

export const getColor = (color: string | undefined, name: string) =>
  color && color !== "#000000" && color !== "" ? color : colorFromName(name);

export const COUNTRY_CONFIG = {
  RO: { label: "RO", color: "#1890ff" },
  GE: { label: "GE", color: "#ff4d4f" },
} as const;

export type Resolution = "day" | "week" | "month";

export const getResolution = (start: string, end: string): Resolution => {
  const days = moment(end, DISPLAY_DATE_FORMAT).diff(
    moment(start, DISPLAY_DATE_FORMAT),
    "days",
  );
  if (days <= 31) return "day";
  if (days <= 90) return "week";
  return "month";
};

export const getBucketKey = (date: string, resolution: Resolution): string => {
  if (resolution === "week")
    return moment(date).startOf("isoWeek").format("YYYY-MM-DD");
  if (resolution === "month")
    return moment(date).startOf("month").format("YYYY-MM-DD");
  return moment(date).format("YYYY-MM-DD");
};

export const formatBucketLabel = (
  key: string,
  resolution: Resolution,
): string => {
  if (resolution === "month") return moment(key).format("MMM YYYY");
  if (resolution === "week") return moment(key).format("MMM D");
  return moment(key).format("MMM D");
};

/**
 * Aggregates per-day data from the API into chart-ready buckets.
 *
 * Each bucket corresponds to one bar on the chart (day / week / month depending on resolution).
 * For each bucket we track:
 *  - project durations (groupBy=project) OR total duration (groupBy=user) as numeric keys
 *  - holidayType: the combined holiday flag for all days in that bucket.
 *    If a bucket spans multiple days with different holiday flags, we upgraded to BOTH.
 */
export const buildBarData = (
  days: Day[],
  resolution: Resolution,
  groupBy: "project" | "user",
): BarDataItem[] => {
  const buckets: Record<string, Record<string, number>> = {};
  const bucketHoliday: Record<string, HolidayCountryType> = {};

  days.forEach((day) => {
    const key = getBucketKey(day.date, resolution);

    // we do this to also show empty buckets in the graph
    if (!buckets[key]) buckets[key] = {};
    if (!bucketHoliday[key]) bucketHoliday[key] = HolidayCountryType.NONE;

    // Merge holiday flags: once a bucket has both countries, it stays BOTH.
    const existing = bucketHoliday[key];
    const incoming = day.holidayCountryTypes;
    if (existing === HolidayCountryType.NONE) {
      bucketHoliday[key] = incoming;
    } else if (existing !== incoming && incoming !== HolidayCountryType.NONE) {
      bucketHoliday[key] = HolidayCountryType.BOTH;
    }

    if (groupBy === "project") {
      // Accumulate each project's minutes into its named key.
      day.projects.forEach((p) => {
        buckets[key][p.projectName] =
          (buckets[key][p.projectName] ?? 0) + p.durationMinutes;
      });
    } else {
      // Accumulate each user's minutes into their named key.
      day.users.forEach((u) => {
        buckets[key][u.fullName] =
          (buckets[key][u.fullName] ?? 0) + u.durationMinutes;
      });
    }
  });

  return Object.entries(buckets)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, values]) => ({
      date: formatBucketLabel(key, resolution),
      holidayType: bucketHoliday[key] ?? HolidayCountryType.NONE,
      ...values,
    }));
};
